import * as Tooltip from "@radix-ui/react-tooltip";
import React, { useCallback, useState } from "react";
import { OnboardingIconPosition, OnboardingIconType } from "../../../models/onboarding-tooltip";
import { AnimatedIconType, OnboardingAnimatedIcon } from "../../onboarding-tooltip/onboarding-animated-icon";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../typography/SmallText/SmallText";
import "./kuika-onboarding-tooltip.scss";

export interface IOnboardingStep {
  label?: string;
  header?: string;
  image?: string;
  selector?: string;
}

export interface KuikaOnboardingTooltipProps {
  children: React.ReactNode;
  label?: string;
  header?: string;
  image?: string;
  steps?: IOnboardingStep[];
  learnMoreUrl?: string;
  position?: "top" | "right" | "bottom" | "left";
  triggerType?: "hover" | "click" | "auto" | "hint" | "always";
  backgroundColor?: string;
  textColor?: string;
  width?: number;
  height?: number;
  showCloseButton?: boolean;
  onClose?: () => void;
  onDone?: () => void;
  delayDuration?: number;
  sideOffset?: number;
  align?: "start" | "center" | "end";
  onContentMouseEnter?: () => void;
  onContentMouseLeave?: () => void;
  onStepChange?: (stepIndex: number) => void;
  initialStep?: number;
  icon?: OnboardingIconType | "";
  iconColor?: string;
  iconSize?: number;
  iconPosition?: OnboardingIconPosition;
  tooltipOffsetX?: number;
  tooltipOffsetY?: number;
  isInModal?: boolean;
}

const formatTextWithKeys = (text?: string) => {
  if (!text) return text;
  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  return text.replace(/\{\{cmdKey\}\}/g, isMac ? "CMD" : "CTRL").replace(/\{\{altKey\}\}/g, isMac ? "OPT" : "ALT");
};

const KuikaOnboardingTooltip: React.FC<KuikaOnboardingTooltipProps> = ({
  children,
  label,
  header,
  image,
  steps,
  learnMoreUrl,
  position = "top",
  triggerType = "hover",
  backgroundColor,
  textColor,
  width,
  height,
  showCloseButton = true,
  onClose,
  onDone,
  delayDuration = 300,
  sideOffset = 8,
  align = "center",
  onContentMouseEnter,
  onContentMouseLeave,
  onStepChange,
  initialStep = 0,
  icon,
  iconColor,
  iconSize = 20,
  iconPosition = "right-center",
  tooltipOffsetX,
  tooltipOffsetY,
  isInModal = false
}) => {
  const [isOpen, setIsOpen] = useState(triggerType === "auto" || triggerType === "hint" || triggerType === "always");
  const [currentStep, setCurrentStep] = useState(initialStep);

  React.useEffect(() => {
    if (triggerType === "auto" || triggerType === "hint" || triggerType === "always") {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [triggerType]);

  const hasSteps = steps && steps.length > 0;
  const totalSteps = hasSteps ? steps.length : 0;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const currentContent = hasSteps ? steps[currentStep] : { label, header, image };

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (triggerType === "hover" && !hasSteps) {
        setIsOpen(open);
      }
    },
    [triggerType, hasSteps]
  );

  const handleTriggerClick = useCallback(() => {
    if (triggerType === "click") {
      setIsOpen((prev) => !prev);
    }
  }, [triggerType]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
    onClose?.();
  }, [onClose]);

  const handleDone = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
    onDone?.();
  }, [onDone]);

  const handlePrevStep = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep((prev) => {
        const newStep = prev - 1;
        onStepChange?.(newStep);
        return newStep;
      });
    }
  }, [isFirstStep, onStepChange]);

  const handleNextStep = useCallback(() => {
    if (!isLastStep) {
      setCurrentStep((prev) => {
        const newStep = prev + 1;
        onStepChange?.(newStep);
        return newStep;
      });
    }
  }, [isLastStep, onStepChange]);

  const handleLearnMore = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (learnMoreUrl) {
        window.open(learnMoreUrl, "_blank", "noopener,noreferrer");
      }
    },
    [learnMoreUrl]
  );

  const contentStyle: React.CSSProperties = {
    ...(backgroundColor && { backgroundColor }),
    ...(textColor && { color: textColor }),
    ...(width && { width: `${width}px`, maxWidth: `${width}px` }),
    ...(height && { height: `${height}px` }),
    ...((tooltipOffsetX || tooltipOffsetY) && {
      transform: `translate(${tooltipOffsetX ?? 0}px, ${tooltipOffsetY ?? 0}px)`
    })
  };

  const hasContent = currentContent.label || currentContent.header || currentContent.image;

  if (!hasContent && !hasSteps) {
    return <>{children}</>;
  }

  const getImageSrc = (imageData?: string): string | undefined => {
    if (!imageData) return undefined;
    if (imageData.startsWith("data:image/")) return imageData;
    if (imageData.startsWith("http://") || imageData.startsWith("https://")) return imageData;
    return `data:image/png;base64,${imageData}`;
  };

  const renderContent = () => (
    <div className="kuika-onboarding-tooltip__inner">
      {currentContent.image && (
        <div className="kuika-onboarding-tooltip__image-container">
          <img
            src={getImageSrc(currentContent.image)}
            alt={currentContent.header || "Onboarding"}
            className="kuika-onboarding-tooltip__image"
          />
        </div>
      )}
      {currentContent.header && (
        <KuikaSmallMediumText style={textColor ? { color: textColor } : undefined}>
          {formatTextWithKeys(currentContent.header)}
        </KuikaSmallMediumText>
      )}
      {currentContent.label && (
        <KuikaSmallText style={textColor ? { color: textColor } : undefined}>
          {formatTextWithKeys(currentContent.label)}
        </KuikaSmallText>
      )}
    </div>
  );

  const renderFooter = () => {
    const showLeftButtons = learnMoreUrl || hasSteps;
    const showRightButtons = hasSteps;

    if (!showLeftButtons && !showRightButtons) {
      return null;
    }

    return (
      <div className="kuika-onboarding-tooltip__footer">
        <div className="kuika-onboarding-tooltip__footer-left">
          {learnMoreUrl && (
            <KuikaButton type="text" size="sm" color="default" onClick={handleLearnMore}>
              Learn More
            </KuikaButton>
          )}
          {hasSteps && !isLastStep && (
            <KuikaButton type="text" size="sm" color="neutral" onClick={handleClose}>
              CLOSE
            </KuikaButton>
          )}
          {hasSteps && isLastStep && (
            <KuikaButton type="text" size="sm" color="default" onClick={handleDone}>
              DONE
            </KuikaButton>
          )}
          {triggerType === "click" && !hasSteps && (
            <KuikaButton type="text" size="sm" color="neutral" onClick={handleClose}>
              CLOSE
            </KuikaButton>
          )}
        </div>
        {hasSteps && (
          <div className="kuika-onboarding-tooltip__footer-right">
            <div className="kuika-onboarding-tooltip__navigation">
              <KuikaButton
                type="text"
                size="sm"
                isIcon
                disabled={isFirstStep}
                onClick={handlePrevStep}
                className="kuika-onboarding-tooltip__nav-button"
                aria-label="Previous step"
                tabIndex={0}
              >
                <KuikaSvg name="chevron-left" type="arrows" width={20} height={20} disabled={isFirstStep} />
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                isIcon
                disabled={isLastStep}
                onClick={handleNextStep}
                className="kuika-onboarding-tooltip__nav-button"
                aria-label="Next step"
                tabIndex={0}
              >
                <KuikaSvg name="chevron-right" type="arrows" width={20} height={20} disabled={isLastStep} />
              </KuikaButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderIcon = () => {
    if (!icon) return null;

    return (
      <div
        className={`kuika-onboarding-tooltip__icon kuika-onboarding-tooltip__icon--${iconPosition}`}
        style={{ color: iconColor || "var(--blue-6)" }}
      >
        {icon.startsWith("animated-") ? (
          <OnboardingAnimatedIcon
            type={icon.replace("animated-", "") as AnimatedIconType}
            size={iconSize}
            color={iconColor || "var(--blue-6)"}
          />
        ) : (
          <KuikaSvg name={icon} type="common" width={iconSize} height={iconSize} color={iconColor || "var(--blue-6)"} />
        )}
      </div>
    );
  };

  const trigger = !icon ? (
    <Tooltip.Trigger asChild onClick={handleTriggerClick}>
      {children}
    </Tooltip.Trigger>
  ) : (
    <div className="kuika-onboarding-tooltip__trigger-wrapper">
      {children}
      <Tooltip.Trigger asChild onClick={handleTriggerClick}>
        {renderIcon()}
      </Tooltip.Trigger>
    </div>
  );

  return (
    <Tooltip.Provider delayDuration={triggerType === "hover" ? delayDuration : 300}>
      <Tooltip.Root open={isOpen} onOpenChange={handleOpenChange}>
        {trigger}
        <Tooltip.Portal>
          <Tooltip.Content
            data-testid="onboardingTooltipContent"
            className={`kuika-onboarding-tooltip__content ${
              isInModal ? "kuika-onboarding-tooltip__content--in-modal" : ""
            }`}
            style={contentStyle}
            sideOffset={sideOffset}
            side={position}
            align={align}
            onMouseEnter={onContentMouseEnter}
            onMouseLeave={onContentMouseLeave}
            onPointerDownOutside={(e) => {
              // Prevent default for hover to keep tooltip open
              // Also prevent default for click type to allow interactive elements (like Learn More) to work
              if (triggerType === "hover" || triggerType === "click") {
                e.preventDefault();
              }
            }}
          >
            <div className="kuika-onboarding-tooltip__content-inner">
              {renderContent()}
              {renderFooter()}
            </div>
            <Tooltip.Arrow className={`kuika-onboarding-tooltip__arrow kuika-onboarding-tooltip__arrow--${position}`} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default KuikaOnboardingTooltip;
